/**
 * Canonical contract for where a submission's score lives.
 *
 * A submission never carries a top-level `score` field — the value is nested
 * inside `submittedData` (a.k.a. survey `responses`) under one of these keys.
 * Keeping the list here means the Daily Journey history table and the
 * SubmissionViewer's FormDisplay read the exact same candidates and cannot
 * drift apart (SIMS-7103).
 */
export const SCORE_FIELD_CANDIDATES = [
	"totalScore",
	"score",
	"calculatedScore",
] as const;

/**
 * Pull the numeric score out of a submission's `submittedData`/`responses`
 * record, trying each candidate key in order.
 *
 * Returns `undefined` when there is no score (no record, or none of the
 * candidate keys hold a number). Note that `0` is a VALID score and is
 * returned as `0` — callers must use a nullish check (`?? "-"`) rather than a
 * falsy check when rendering a fallback.
 */
export const extractScore = (
	submittedData: Record<string, unknown> | undefined | null,
): number | undefined => {
	if (!submittedData) return undefined;
	for (const key of SCORE_FIELD_CANDIDATES) {
		const value = submittedData[key];
		if (typeof value === "number" && !Number.isNaN(value)) {
			return value;
		}
	}
	return undefined;
};
